import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getAuthToken, setAuthToken, getCachedAccount, setCachedAccount } from "./api";
import { invalidateAllApiCache } from "./apiCache";
import { clearAppLocalStorage } from "./appStorage";
import { signOutSupabase } from "./supabaseAuth";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  // false = signed-out, {...} = signed-in account (organizers row). API JSON key remains `organizer`.
  const [account, setAccount] = useState(() => (getAuthToken() ? getCachedAccount() : false));
  const [checking, setChecking] = useState(() => !!getAuthToken());

  const refresh = useCallback(async () => {
    const tok = getAuthToken();
    if (!tok) {
      setAccount(false);
      setChecking(false);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      const next = r.data.organizer;
      setAccount(next);
      setCachedAccount(next);
    } catch {
      setAuthToken(null);
      setCachedAccount(null);
      setAccount(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** Accepts API alias `organizer` or preferred `account`. */
  function signIn({ token, account: accountArg, organizer }) {
    const next = accountArg ?? organizer;
    setAuthToken(token);
    setCachedAccount(next);
    setAccount(next);
    void invalidateAllApiCache();
  }
  function signOut() {
    clearAppLocalStorage();
    setAccount(false);
    signOutSupabase();
    void invalidateAllApiCache();
  }
  function updateAccount(o) {
    setAccount(o);
    setCachedAccount(o);
  }

  const value = { account, checking, signIn, signOut, refresh, updateAccount };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
